import './styles.css';
import {
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogContentText,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import Dialog from "@mui/material/Dialog"
import { Delete } from "@mui/icons-material";
import moment from "moment";
import { FC, useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";
import { distribuidores$, revendas$ } from "src/store/global.store";
import { apiNoLoading, IDataRequest } from "src/framework/providers/api";
import theme from "src/theme/light";
import { UploadFile } from "./Upload";
import { ProjetosProvider } from "src/providers/ProjetosProvider";
import { notifierStore } from "src/framework/store/NotifierStore";

interface UploadDialogProps {
  disabled?: boolean
  openDialog: boolean
  close: () => void
  formState: any
  onChange: (uploads: any) => void
  onCapturaCodigoVenda: (codigoVenda: string) => void
}

export type AnexoFile = {
  index?: number;
  descricao?: string;
  file?: File;
  previewName?: string | null;
  progress?: number
}

const useStyles = makeStyles({
  exampleText: {
    color: "#CC8F00",
    fontStyle: 'italic'
  }
})

const projetosProvider = new ProjetosProvider();

const UploadDialog: FC<UploadDialogProps> = ({ onCapturaCodigoVenda, onChange, openDialog, formState, close, disabled = false }) => {
  const classes = useStyles()

  const [uploading, setUploading] = useState<boolean>(false);
  const [hasError, setHasError] = useState<number[]>([]);
  const [anexosFileList, setAnexosList] = useState<AnexoFile[]>([])

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const FooterBottom = isMobile ? Box : DialogActions;

  const { getRootProps, getInputProps } = useDropzone({
    disabled: disabled,
    onDropAccepted: (_files) => {
      const _anexoList: AnexoFile[] = [];
      let count: number = 1;
      for (let file of _files) {
        const now: string = moment().format("YYYYMMDDHHmmss");
        const descricao = String(file.name || "").split('.').slice(0, -1).join('.');

        _anexoList.push({
            index: count - 1,
            file: file,
            previewName: `ARQ-[CODIGO_PROJETO]-${now}-${count++}`,
            descricao,
        })
      }
      setAnexosList([...anexosFileList, ..._anexoList]);
    }
  });

  const upload = async (): Promise<void> => {
    const result: any = {}
    try {
        setUploading(true)
        let codigoProjeto = formState?.codigo_venda || "";
        if (codigoProjeto === "") {
          let cpfCnpj = formState.distribuidor?.cnpj_cpf

          if (cpfCnpj === undefined) {
            const distribuidores = distribuidores$.getValue()?.data || []

            const idxDistribuidor = distribuidores.findIndex((d) => {
              return d.id === formState.distribuidor_id
            })

            if (idxDistribuidor > -1) {
              formState.distribuidor = distribuidores[idxDistribuidor]
              cpfCnpj = formState.distribuidor?.cnpj_cpf;
            }

            if (formState.revenda_id) {
              const revendas = revendas$.getValue()?.data || []

              const idxRevenda = revendas.findIndex((r) => {
                return r.id === formState.revenda_id;
              })

              if (idxRevenda > -1) {
                formState.revenda = revendas[idxRevenda];
                cpfCnpj = formState.revenda?.cnpj_cpf
              }
            }
          }

          if (cpfCnpj) {
              const captura = await projetosProvider.capturar({ 
                cpfCnpjDistribuidor: formState.distribuidor?.cnpj_cpf, 
                tipo: formState.tipo, 
                segmento: 'vertical_ongrid', 
                potencia: formState.potencia,
                engenharia: true,
                codigo_venda: formState?.codigo_venda || '',
              });
              codigoProjeto = captura.codigo_venda;
              formState.codigo_venda = codigoProjeto 
              onCapturaCodigoVenda(codigoProjeto)
          } else {
              notifierStore.setNotifier({
                title: "Aviso!!!",
                content: `CPF/CNPJ do distribuidor ${formState.distribuidor}`,
                timeOut: 3000,
                onClose: () => {
                    notifierStore.setOpen(false);
                }
              })
              notifierStore.setOpen(true);
          }

      }

      const url: string = `/projetos/anexos/${codigoProjeto}`;
      const requestList: IDataRequest[] = [];

      for (let idxAnexo in anexosFileList) {
        const form = new FormData();
        if (anexosFileList[idxAnexo]?.file !== undefined) {
          const _file: any = anexosFileList[idxAnexo];
          const extension: string = _file.file.name.split(".").pop();
          const filename = `${anexosFileList[idxAnexo].previewName?.replace("[CODIGO_PROJETO]", codigoProjeto)}.${extension}`;
          form.append(filename, _file.file, _file.file.name);
          result[`${filename}`] = { success: {}, error: {}, descricao: anexosFileList[idxAnexo].descricao }

          const request: IDataRequest = {
            url: url,
            data: form,
            onUploadProgress: (progressEvent: any) => {
              const progress: number = Math.round(
                  (progressEvent.loaded * 100) / progressEvent.total
              );
              anexosFileList[idxAnexo].progress = progress
              setAnexosList([...anexosFileList])
            }
          }

          requestList.push(request)
        }
      }

      const responses = await Promise.all([...requestList.map((r) => {
        return apiNoLoading.post(r)
      })])

      const keys = Object.keys(result);

      for (let response of responses) {
        const responseKey = Object.keys(response.data).shift();
        result[String(responseKey)].success = response.data[String(responseKey)]
      }
      onChange(keys.map((idx) => {
        return {
          url: result[idx].success.path,
          descricao: result[idx].descricao,
          nome: idx.split(".").shift(),
          "@new": true
        }
      }))

      setAnexosList([])
    } catch (error) {

    } finally {
      setUploading(false)
      close()
    }
  }

  const nomesAnexos = useMemo(()=>{
    return anexosFileList?.map((a) => `${a.index}:${a?.descricao}`)
  }, [anexosFileList])

  return (
    <Dialog open={openDialog} maxWidth="xl" style={{ minWidth: isMobile ? '100%' : "1024px" }} className="modal-upload">
      <Box p={2} width={isMobile ? "100%" : "95%"}>
        <Typography id="verticais-upload-dialog-title" variant="h5" color="textPrimary">Adicionar anexo</Typography>
      </Box>
      <Divider />
      <Box p={2} width={isMobile ? "100%" : "95%"}>
        <Typography noWrap={false} variant="subtitle2" color="textSecondary">Se seus arquivos forem muito grandes, você pode inseri-los compactado no espaço abaixo. Basta estar renomeando o arquivo incluindo ".zip" ao final.</Typography>
        <Typography className={classes.exampleText} variant="subtitle2">Exemplo: Projeto_Vertical_Intelbras.zip</Typography>
      </Box>
      <DialogContent style={{ padding: "5px" }}>
        <DialogContentText >
          <div className="row">
            <div className="X12">
              <section className="container">
                <div {...getRootProps({ className: 'dropzone' })} style={{
                  display: "flex",
                  height: isMobile ? "180px" : "100px",
                  color: `${theme.palette.grey[500]}`,
                  border: `dotted 2px ${theme.palette.grey[200]}`,
                  background: 'transparent',
                  borderRadius: theme.shape.borderRadius,
                  textAlign: "center",
                  alignContent: "center",
                  alignItems: "center",
                  justifyItems: "center",
                  justifyContent: "center",
                  cursor: 'pointer'
                }}>
                  <input {...getInputProps()} accept=".pdf" />
                  <div style={{ padding: "0px 15px" }}>
                    <Typography style={{ color: '#627F87', fontWeight: 500 }} variant='body1'>Quer adicionar mais arquivos ?
                    </Typography>
                    <Typography variant='body2' color="textSecondary">
                      Clique aqui para carregar um ou mais arquivos.
                    </Typography>
                    <Typography variant='body2' color="textSecondary">
                      Arraste e solte aqui um ou mais arquivos.
                    </Typography>
                  </div>
                </div>
                {!!anexosFileList.length && (
                <>
                  <aside>
                    <Box 
                      mt={2} 
                      px={2} 
                      display='flex' 
                      justifyContent={isMobile ? "space-between" : "flex-start" }
                      alignItems='center' 
                      bgcolor={theme.palette.grey[200]} 
                      height="56px"
                    >
                      <Typography style={{ fontWeight: 600 }} variant="body2" color='textPrimary'>
                        {anexosFileList.length} {anexosFileList.length > 1 ? 'arquivos' : 'arquivo'} {anexosFileList.length > 1 ? 'selecionados' : 'selecionado'}
                      </Typography>
                      {isMobile && (
                        <Tooltip title={"Excluir todos os arquivos"}>
                          <IconButton size="small" style={{ color: theme.palette.error.main }} onClick={() => setAnexosList([])}>
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                    <Table size="small" className="table-upload">
                      <TableHead>
                        <TableRow>
                          <TableCell>Nome do arquivo</TableCell>
                          <TableCell>* Descrição do arquivo</TableCell>
                          {!isMobile && (
                            <TableCell align="right">
                              <Tooltip title={"Excluir todos os arquivos"}>
                                <IconButton size="small" style={{ color: theme.palette.error.main }} onClick={() => setAnexosList([])}>
                                  <Delete />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          )}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {anexosFileList.map((anexo: any, index: number) => {
                          return <UploadFile
                            key={`verticais-pre-upload-file-${formState?.codigo_venda}-${index}`}
                            anexo={anexo}
                            index={index}
                            nomesAnexos={nomesAnexos}
                            disabled={uploading}
                            onRemove={(index) => {
                              anexosFileList.splice(index, 1)
                              setAnexosList([...anexosFileList])
                            }}
                            onChange={(descricao, index) => {
                              anexosFileList[index].descricao = descricao
                              setAnexosList([...anexosFileList])
                            }}
                            hasError={(_error, _index) => {
                              const errorState: number[] = [...hasError]

                              if (_error) {
                                  errorState.push(index);
                                  setHasError(errorState)
                              } else {
                                  setHasError(errorState.filter((n) => n !== _index))
                              }
                            }}
                          />
                        })}
                      </TableBody>
                    </Table>
                  </aside>
                </>)}
              </section>
            </div>
          </div>
        </DialogContentText>
      </DialogContent>
      <FooterBottom className="footer-upload" style={isMobile ? { display: "flex", flexDirection: "column-reverse", width: "100%" } : {}}>
        <Button
          fullWidth={isMobile}
          disabled={uploading}
          onClick={close}
          color="secondary"
          variant={isMobile ? 'outlined' : 'text'}
        >
          Cancelar
        </Button>
        <Button
          fullWidth={isMobile}
          disabled={!anexosFileList.length}
          onClick={() => {
            upload().then(() => {
                // setOpenDialog(false)
            })
          }}
          color="primary" variant="contained" disableElevation>
          {uploading ? <><CircularProgress variant="indeterminate" size={20} /></> : "Carregar arquivo(s)"}
        </Button>
      </FooterBottom>
  </Dialog>
  )
}

export { UploadDialog }